import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Search, RefreshCw, UserPlus, Trash2, Radar, X, Upload, CheckCircle2, AlertTriangle, ChevronRight, Fingerprint, Ear } from 'lucide-react';
import { getToken, api } from '../lib/api';
import { parsePnpCsv, type PnpCsvRow } from '../lib/pnpCsv';
import { needsGdmsZeroTouch } from '../lib/phoneLocality';
import { prioritiseVendor } from '../lib/phoneModels';
import { Dialog } from '../components/Dialog';
import { ProvisionProgressModal } from '../components/ProvisionProgressModal';
import { useToast } from '../components/Toast';

const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });
const fJson = async (url: string, init: RequestInit = {}) => {
  const r = await fetch(url, { ...init, headers: { ...auth().headers, ...(init.headers || {}) } });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
};

// DP720/DP730/DP722 are DECT handsets that register wirelessly to a DP750 base.
// They can appear in the discovered-phones list (SIP registration from the base
// or ARP) but must not be confused with the base station itself.
const DECT_HANDSET_RE = /\bdp\s*7[23]\d\b/i;
const isDectHandset = (model: string | null | undefined) =>
  !!model && DECT_HANDSET_RE.test(model);
// DECT BASE stations (DP750/752/755): infrastructure hosting up to 10 handsets.
// Mirrors the server's isDectModel minus the handset models above — a base can be
// set up with NO user; users attach per-handset afterwards (#799).
const isDectBaseModel = (model: string | null | undefined) =>
  !!model && /\bdp\s*7[15]\d\b/i.test(model) && !DECT_HANDSET_RE.test(model);

function timeAgo(ts: string) {
  const ms = Date.now() - new Date(ts).getTime();
  if (ms < 60_000) return 'just now';
  const m = Math.floor(ms / 60_000);
  if (m < 60) return `${m} minute${m === 1 ? '' : 's'} ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
  const d = Math.floor(h / 24);
  return `${d} day${d === 1 ? '' : 's'} ago`;
}

export default function PnpPhones() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [assignTarget, setAssignTarget] = useState<any | null>(null);
  const [provisioning, setProvisioning] = useState<{ mac: string; label?: string } | null>(null);
  const [scan, setScan] = useState<any | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [listenOpen, setListenOpen] = useState(false);
  const scanPollRef = useRef<any>(null);
  const [phonesOnly, setPhonesOnly] = useState(true);
  const [sort, setSort] = useState<{ key: string; dir: 1 | -1 }>({ key: 'last_seen', dir: -1 });
  const [locFilter, setLocFilter] = useState('');

  const list = useQuery<any[]>({
    queryKey: ['discovered-phones'],
    queryFn: () => fJson('/api/phones/discovered'),
    refetchInterval: 5000,
  });
  const users = useQuery<any[]>({
    queryKey: ['users-list'],
    queryFn: () => fJson('/api/users'),
  });
  const tenants = useQuery<any[]>({ queryKey: ['tenants'], queryFn: () => fJson('/api/tenants') });

  // A phone = SIP evidence (model / OPTIONS User-Agent / SIP-PnP-active_scan hit), already assigned,
  // OR a device from a known phone vendor (Grandstream vendor_key, or a phone-vendor MAC OUI). The OUI
  // signal matters for onboarding: a just-plugged-in / factory-reset phone has no SIP account yet, so
  // it never answers OPTIONS and is only seen via ARP — excluding OUI-only hid exactly the phones you
  // came to add. The rare cost is a Grandstream GWN AP/ATA showing under "Phones only"; worth it.
  const PHONE_OUIS = ['ec74d7', 'c074ad', '000b82', '00260b', 'accf23', '2c54cf', 'acd9cc', '001565', '000fd5', '805ec0', '0004f2', '64167f', '00907a', 'e0d173'];
  const isPhone = (p: any) =>
    !!(p.model && p.model.toLowerCase() !== 'unknown')
    || !!(p.sip_user_agent && String(p.sip_user_agent).trim())
    || /sip|pnp|active_scan/i.test(p.discovered_via || '')
    || p.status === 'assigned'
    || (p.vendor_key && String(p.vendor_key).toLowerCase() === 'grandstream')
    || PHONE_OUIS.includes(String(p.mac_address || '').replace(/[^a-f0-9]/gi, '').slice(0, 6).toLowerCase());
  // Discovery noise: multicast / reserved 224-239.x are NOT real devices.
  const isMulticast = (ip: any) => { const o = parseInt(String(ip || '').split('.')[0], 10); return o >= 224 && o <= 239; };
  // Location dropdown options = real named locations (SBC / department), never raw subnets.
  const locations = Array.from(new Set((list.data || []).filter((p: any) => !isMulticast(p.ip_address)).map((p: any) => p.sbc_name).filter(Boolean))).sort();
  // Location name -> SBC node id (from any row that carries one) so Scan can target
  // that SBC's own LAN sweep instead of pointlessly sweeping the PBX's local subnets.
  const locNodeId: Record<string, string> = {};
  for (const p of (list.data || [])) {
    if (p.sbc_name && p.sbc_node_id && !locNodeId[p.sbc_name]) locNodeId[p.sbc_name] = p.sbc_node_id;
  }
  const filtered = (list.data || [])
    .filter(p => !isMulticast(p.ip_address))
    .filter(p => !phonesOnly || isPhone(p))
    .filter(p => !locFilter || p.sbc_name === locFilter)
    .filter(p =>
      !search || (p.mac_address || '').toLowerCase().includes(search.toLowerCase())
              || (p.model || '').toLowerCase().includes(search.toLowerCase())
              || (p.vendor_key || '').toLowerCase().includes(search.toLowerCase()));

  // Sortable columns — click a header. IP sorts numerically, When by timestamp, rest A-Z.
  function sortVal(p: any, key: string): number | string {
    if (key === 'last_seen') return p.last_seen ? new Date(p.last_seen).getTime() : 0;
    if (key === 'ip_address') {
      const o = String(p.ip_address || '').split('.').map(Number);
      return o.length === 4 && o.every(n => !isNaN(n)) ? (o[0] * 2 ** 24 + o[1] * 2 ** 16 + o[2] * 2 ** 8 + o[3]) : 0;
    }
    if (key === 'location') return (p.sbc_name || p.ip_address || '').toLowerCase();
    return String(p[key] || '').toLowerCase();
  }
  const sorted = [...filtered].sort((a, b) => {
    const va = sortVal(a, sort.key), vb = sortVal(b, sort.key);
    return va < vb ? -sort.dir : va > vb ? sort.dir : 0;
  });
  const toggleSort = (key: string) =>
    setSort(s => (s.key === key ? { key, dir: (s.dir === 1 ? -1 : 1) as 1 | -1 } : { key, dir: 1 }));

  const del = useMutation({
    mutationFn: async () => {
      for (const id of selected) await fJson(`/api/phones/discovered/${id}`, { method: 'DELETE' });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['discovered-phones'] }); setSelected(new Set()); toast('success', 'Removed from discovery'); },
  });

  // Identity probes (#803): turn unknown/? rows into exact models without web
  // credentials (FS registration UA -> SIP OPTIONS -> HTTP fingerprint).
  const identifyAll = useMutation({
    mutationFn: () => fJson('/api/phones/identify-unknowns', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }),
    onSuccess: (r: any) => {
      qc.invalidateQueries({ queryKey: ['discovered-phones'] });
      toast(r.identified > 0 ? 'success' : 'info', `Probed ${r.probed} unknown device(s), identified ${r.identified}`);
    },
    onError: (e: any) => toast('error', `Identify failed: ${e.message}`),
  });
  const identifyOne = useMutation({
    mutationFn: (id: string) => fJson(`/api/phones/discovered/${id}/identify`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }),
    onSuccess: (r: any) => {
      qc.invalidateQueries({ queryKey: ['discovered-phones'] });
      if (r.result === 'identified') {
        const src = r.source === 'register_ua' ? 'from registration'
          : r.source === 'options_probe' ? 'SIP probe'
          : r.source === 'http_probe' ? 'web fingerprint'
          : /^sbc_/.test(r.source || '') ? `via site SBC (${String(r.source).replace('sbc_', '')})`
          : r.source;
        toast('success', `${r.mac}: ${r.model} (${src})`);
      }
      else if (r.result === 'remote-site') toast('info', `${r.mac} is behind a remote site — ${r.sbc_reason === 'arp-mismatch' ? 'the site SBC could not confirm that IP belongs to this phone' : "the site SBC couldn't identify it"}. Reboot it with "Listen for a phone", or it identifies itself when it registers.`);
      else if (r.result === 'no-answer') toast('info', `${r.mac}: no answer${r.hint ? ` — web server suggests ${r.hint === 'ht80x-family' ? 'an HT80x ATA' : 'a modern GRP/WP/DP-series UI'}` : ''}. If it is a DP750, power-cycle it — DECT bases only announce at boot.`);
      else toast('info', `${r.mac}: ${r.result}`);
    },
    onError: (e: any) => toast('error', `Identify failed: ${e.message}`),
  });

  const assign = useMutation({
    mutationFn: ({ id, ext, dom, label, model, sn, remote, rpsProfile, dectBase }: any) => fJson('/api/phones/assign', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      // sn/remote/rpsProfile are threaded for the GDMS-aware assign flow (#642); the
      // current assign handler ignores unknown fields, so this is forward-compatible.
      // dect_base (#799): a DP750 base may be set up with NO extension — users are
      // attached per-handset afterwards (Phones → Handsets).
      body: JSON.stringify({ discovered_phone_id: id, extension_uuid: ext || undefined, domain_uuid: dom, label, model, sn, remote, rpsProfile, dect_base: dectBase || undefined }),
    }),
    onSuccess: async (data: any, vars: any) => {
      qc.invalidateQueries({ queryKey: ['discovered-phones'] });
      // Grandstream zero-touch: if the operator captured a serial, register the MAC
      // in GDMS + issue the RPS redirect now (a factory/remote phone needs this to
      // pull config on first boot). Surfaces bound-elsewhere / not-ready clearly.
      if (vars?.sn && data?.mac_address) {
        try {
          const r = await api.devices.enroll({
            mac: data.mac_address, sn: vars.sn, domain_uuid: vars.dom,
            remote: vars.remote, rpsProfile: vars.rpsProfile,
          });
          const st = r?.upstream?.gdms?.status || r?.upstream?.status;
          if (st === 'bound-elsewhere') toast('error', 'Enrolled locally, but this MAC is bound elsewhere in GDMS (e.g. the operator 3CX) — release it there. On-LAN PnP still works.');
          else if (st === 'sn-missing') toast('error', 'GDMS rejected the serial — double-check the SN.');
          else if (r?.upstream?.skipped) toast('info', 'Assigned. GDMS enroll skipped (creds not configured on server).');
          else toast('success', 'Assigned + enrolled in GDMS/RPS');
        } catch (e: any) { toast('error', `Assigned, but GDMS enroll failed: ${e.message}`); }
      }
      if (data?.mac_address) {
        // Refresh the (already-open) progress modal with the server-confirmed label. The modal
        // opens at click time now (#685) so the operator sees live progress instead of a frozen
        // "Assigning…" button while the endpoint works.
        setProvisioning({ mac: data.mac_address, label: vars?.label || (data.extension ? `Extension ${data.extension}` : undefined) });
      } else {
        toast('success', 'Assigned');
      }
    },
    // Assign failed outright — close the optimistic progress modal so it can't sit on
    // "Assigning extension…" for a phone that was never assigned.
    onError: (e: any) => { setProvisioning(null); toast('error', e.message); },
  });

  // Active LAN scan: kick off, then poll progress every 1s until done.
  // With a Location selected, the scan targets THAT SBC (it sweeps its own remote
  // LAN via the heartbeat scan_now channel) instead of the PBX's local subnets —
  // the PBX can't see a branch office's network, so the old global sweep never
  // found remote phones and probed every other site for nothing.
  const startScan = useMutation({
    mutationFn: () => fJson('/api/phones/active-scan', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(locFilter && locNodeId[locFilter] ? { sbc_node_id: locNodeId[locFilter] } : {}),
    }),
    onSuccess: (r: any) => {
      setScan({ id: r.scan_id, mode: r.mode, sbc_name: r.sbc_name, scanned: 0, found: 0, candidates: 0, done: false });
      scanPollRef.current = setInterval(async () => {
        try {
          const s = await fJson(`/api/phones/active-scan/${r.scan_id}`);
          setScan(s);
          qc.invalidateQueries({ queryKey: ['discovered-phones'] });
          if (s.done) {
            clearInterval(scanPollRef.current);
            scanPollRef.current = null;
            toast('success', s.mode === 'sbc'
              ? `${s.sbc_name} sweep complete — ${s.found} phone${s.found === 1 ? '' : 's'} reported`
              : `Scan complete — ${s.found} phone${s.found === 1 ? '' : 's'} found from ${s.scanned} hosts probed`);
          }
        } catch (e: any) {
          clearInterval(scanPollRef.current);
          scanPollRef.current = null;
          setScan((cur: any) => cur ? { ...cur, done: true, error: e.message } : null);
        }
      }, 1000);
    },
    onError: (e: any) => toast('error', `Scan failed: ${e.message}`),
  });

  // Cleanup on unmount
  useEffect(() => () => { if (scanPollRef.current) clearInterval(scanPollRef.current); }, []);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/phones')} className="btn-ghost inline-flex items-center gap-1"><ArrowLeft size={14} /> Phones</button>
        <h1 className="text-xl font-semibold">PNP Phones</h1>
        <a href="#" className="ml-auto text-primary text-sm hover:underline">Help</a>
      </div>

      <div className="flex gap-2 flex-wrap items-center">
        <button
          onClick={() => startScan.mutate()}
          disabled={startScan.isPending || (scan && !scan.done)}
          className="btn-primary inline-flex items-center gap-1"
          title={locFilter && locNodeId[locFilter]
            ? `Ask the ${locFilter} SBC to sweep its own LAN for phones`
            : 'Probe the local network for phones (ARP table + SIP OPTIONS sweep)'}
        >
          <Radar size={14} className={scan && !scan.done ? 'animate-pulse' : ''} />
          {scan && !scan.done
            ? (scan.mode === 'sbc' ? `Sweeping ${scan.sbc_name}… ${scan.found} found` : `Scanning… ${scan.scanned} probed, ${scan.found} found`)
            : (locFilter && locNodeId[locFilter] ? `Scan ${locFilter}` : 'Scan')}
        </button>
        <button
          onClick={() => setListenOpen(true)}
          title="Guided onboarding for a DECT base or any phone that won't show up: watch for its boot-time announcement while you power-cycle it. This is the reliable way to add a DP750 — DECT bases only announce their exact model at boot."
          className="btn-primary inline-flex items-center gap-1"><Ear size={14} /> Listen for a phone</button>
        <button
          disabled={selected.size !== 1}
          onClick={() => {
            const id = [...selected][0];
            setAssignTarget(filtered.find(p => p.id === id));
          }}
          className="btn-primary inline-flex items-center gap-1"><UserPlus size={14} /> Assign</button>
        <button
          onClick={() => setBulkOpen(true)}
          title="Import a CSV / paste of mac,sn,site,extension,label to enroll many phones for zero-touch at once (built for 90-site rollouts)"
          className="btn-primary inline-flex items-center gap-1"><Upload size={14} /> Bulk enroll</button>
        <button disabled={!selected.size} onClick={() => del.mutate()} className="btn-primary inline-flex items-center gap-1 bg-danger"><Trash2 size={14} /> Delete</button>
        <button
          onClick={() => identifyAll.mutate()}
          disabled={identifyAll.isPending}
          title="Probe every unknown/? row: registered phones' User-Agent, a unicast SIP OPTIONS, then the web login page — no admin password needed. Remote-site IPs the PBX can't reach just time out."
          className="btn-primary inline-flex items-center gap-1">
          <Fingerprint size={14} className={identifyAll.isPending ? 'animate-pulse' : ''} />
          {identifyAll.isPending ? 'Identifying…' : 'Identify unknowns'}
        </button>
        <button onClick={() => qc.invalidateQueries({ queryKey: ['discovered-phones'] })} className="btn-ghost inline-flex items-center gap-1"><RefreshCw size={14} /> Refresh</button>
        <label className="inline-flex items-center gap-1.5 text-sm text-textSecondary ml-1 cursor-pointer select-none" title="Hide bare network devices that didn't identify as phones">
          <input type="checkbox" checked={phonesOnly} onChange={e => setPhonesOnly(e.target.checked)} /> Phones only
        </label>

        {scan && (
          <div className={`text-xs ml-2 px-2 py-1 rounded-md font-mono ${scan.done ? (scan.error ? 'bg-danger/10 text-danger' : 'bg-success/10 text-success') : 'bg-primary/10 text-primary'}`}>
            {scan.error
              ? `error: ${scan.error}`
              : scan.mode === 'sbc'
                ? (scan.done
                    ? `${scan.sbc_name} sweep — ${scan.found} phones reported`
                    : `sweeping ${scan.sbc_name} — ${scan.found} found (results appear live)`)
                : scan.done
                  ? `complete — ${scan.found} phones found, ${scan.scanned} hosts probed`
                  : `scan ${scan.id?.slice(0,6) || ''} — ${scan.scanned} probed, ${scan.found} found`}
            {scan.done && (
              <button className="ml-2 hover:opacity-70" onClick={() => setScan(null)}><X size={10} className="inline" /></button>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 max-w-md">
        <Search size={16} className="text-textSecondary" />
        <input className="input flex-1" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…" />
      </div>

      <div className="flex items-center gap-2">
        <span className="text-sm text-textSecondary">Location:</span>
        <select className="input w-auto min-w-[200px]" value={locFilter} onChange={e => setLocFilter(e.target.value)}>
          <option value="">All locations</option>
          {locations.map(loc => <option key={loc} value={loc}>{loc}</option>)}
        </select>
      </div>

      <div className="card p-0 overflow-hidden">
        <table className="table">
          <thead><tr>
            <th></th>
            {([['Vendor','vendor_key'],['Model','model'],['Fw Version','firmware'],['MAC','mac_address'],['When','last_seen'],['IP','ip_address'],['Location','location']] as [string,string][]).map(([label, k]) => (
              <th key={k} className="cursor-pointer select-none hover:text-text" onClick={() => toggleSort(k)}>
                {label}{sort.key === k ? (sort.dir === 1 ? ' ▲' : ' ▼') : ''}
              </th>
            ))}
          </tr></thead>
          <tbody>
            {sorted.map((p: any) => (
              <tr key={p.id} className="hover:bg-elevated">
                <td>
                  <input type="checkbox" checked={selected.has(p.id)} onChange={() => {
                    const next = new Set(selected);
                    if (next.has(p.id)) next.delete(p.id); else next.add(p.id);
                    setSelected(next);
                  }} />
                </td>
                <td>{p.vendor_key ? p.vendor_key.charAt(0).toUpperCase() + p.vendor_key.slice(1) : '—'}</td>
                <td>
                  {(() => {
                    const real = !!p.model && !['unknown', '?', '-', 'grandstream'].includes(String(p.model).toLowerCase());
                    if (real) return p.model;
                    return (
                      <span className="inline-flex items-center gap-1.5">
                        <span className="px-1 py-0.5 rounded text-[10px] font-medium bg-warning/15 text-warning whitespace-nowrap"
                          title="Vendor known from the MAC only — the exact model has not been identified yet.">vendor only</span>
                        <button className="text-primary hover:underline text-xs inline-flex items-center gap-0.5 disabled:opacity-50"
                          disabled={identifyOne.isPending}
                          title="Probe this device now (registration UA, SIP OPTIONS, web fingerprint — no password needed)"
                          onClick={() => identifyOne.mutate(p.id)}>
                          <Fingerprint size={11} /> Identify
                        </button>
                      </span>
                    );
                  })()}
                  {isDectHandset(p.model) && (
                    <span
                      className="ml-1.5 px-1 py-0.5 rounded text-[10px] font-medium bg-primary/15 text-primary whitespace-nowrap"
                      title="DECT wireless handset — registers to a DP750 base station, not directly to the PBX"
                    >DECT handset</span>
                  )}
                  {isDectBaseModel(p.model) && (
                    <span
                      className="ml-1.5 px-1 py-0.5 rounded text-[10px] font-medium bg-success/15 text-success whitespace-nowrap"
                      title="DECT base station — set it up for a department, then attach users as handsets (Phones → Handsets)."
                    >DECT base</span>
                  )}
                </td>
                <td className="font-mono text-xs">{p.firmware || '1.0.x'}</td>
                <td className="font-mono text-xs">{(p.mac_address || '').replace(/:/g, '')}
                  {p.previously_assigned
                    ? <span className="ml-1.5 px-1 py-0.5 rounded bg-warning/15 text-warning font-sans" title="This MAC was assigned before and just re-announced (likely a factory reset). Re-assign / re-provision it.">re-announced</span>
                    : null}</td>
                <td className="text-xs">{p.last_seen ? timeAgo(p.last_seen) : '—'}</td>
                <td className="font-mono text-xs">{p.ip_address}</td>
                {/* Location must be unmistakable: near-identical IPs across sites (x.x.x.78) with no
                    site label is exactly what causes working the wrong-location phone. Local (PBX)
                    phones are on the PBX's own LAN and need no SBC — flag that distinctly. */}
                <td className="text-xs">{p.sbc_name === 'Local (PBX)'
                  ? <span className="px-1.5 py-0.5 rounded bg-success/20 text-success whitespace-nowrap" title="On the PBX's own LAN — provisions zero-touch, no SBC needed.">Local (PBX) · no SBC</span>
                  : p.sbc_name
                    ? <span className="px-1.5 py-0.5 rounded bg-primary/10 text-primary" title="Remote site behind this SBC — provisioned over the SBC tunnel.">{p.sbc_name}</span>
                    : <span className="text-textSecondary" title="No SBC matched this subnet and it is not on the PBX LAN — confirm the site before assigning.">{p.ip_address ? p.ip_address.split('.').slice(0, 3).join('.') + '.x · site unknown' : '—'}</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!sorted.length && (
          <div className="p-6 text-center text-textSecondary">
            {phonesOnly && (list.data || []).some((p: any) => !isPhone(p))
              ? <>No identified phones — {(list.data || []).filter((p: any) => !isPhone(p)).length} unidentified device(s) hidden. Uncheck <b>Phones only</b> to see everything the scan found.</>
              : 'No discovered phones. The SBC populates this list as phones register or appear on the LAN.'}
          </div>
        )}
      </div>

      <Dialog open={!!assignTarget} onClose={() => setAssignTarget(null)} title="Assign Phone" size="md"
        footer={<><button onClick={() => setAssignTarget(null)} className="btn-ghost">Cancel</button><button onClick={() => {
          const t = assignTarget;
          // Open the progress modal IMMEDIATELY (#685) — the assign endpoint can take seconds
          // and the old flow left the operator staring at a static "Assigning…" button. The
          // modal handles the not-yet-assigned stage; onError closes it if the POST fails.
          if (t.mac_address) setProvisioning({ mac: t.mac_address, label: t._label || undefined });
          setAssignTarget(null);
          assign.mutate({ id: t._id, ext: t._ext, dom: t._dom, label: t._label, model: t._model, sn: t._sn, remote: t._remote, rpsProfile: t._rps, dectBase: t._dectBase });
        }} disabled={(!assignTarget?._ext && !(assignTarget?._dectBase && assignTarget?._dom)) || !assignTarget?._model || assign.isPending} className="btn-primary disabled:opacity-50">{assign.isPending ? 'Assigning…' : (assignTarget?._dectBase && !assignTarget?._ext ? 'Set up base' : 'Assign')}</button></>}>
        {assignTarget && (
          <AssignForm target={assignTarget} setTarget={setAssignTarget} users={users.data || []} tenants={tenants.data || []} />
        )}
      </Dialog>

      {provisioning && (
        <ProvisionProgressModal
          mac={provisioning.mac}
          label={provisioning.label}
          onClose={() => { setProvisioning(null); qc.invalidateQueries({ queryKey: ['discovered-phones'] }); }}
        />
      )}

      <BulkEnrollModal open={bulkOpen} onClose={() => setBulkOpen(false)}
        onDone={() => qc.invalidateQueries({ queryKey: ['discovered-phones'] })} />

      {listenOpen && <ListenModal onClose={() => { setListenOpen(false); qc.invalidateQueries({ queryKey: ['discovered-phones'] }); }}
        onAssign={(p: any) => { setListenOpen(false); setAssignTarget(p); }} />}
    </div>
  );
}

// Guided "Listen for a phone" mode (#803). The reliable way to onboard a DP750 (or
// any phone that won't appear): Grandstream devices multicast their EXACT model
// only at BOOT, so we watch the PnP listener while the installer power-cycles the
// device and show its announcement land live. Also surfaces multicast-group health
// so a mis-joined listener (the classic silent-failure) is visible, not guessed.
function ListenModal({ onClose, onAssign }: { onClose: () => void; onAssign: (p: any) => void }) {
  const startedAt = useRef(new Date().toISOString());
  const [elapsed, setElapsed] = useState(0);
  const status = useQuery<any>({
    queryKey: ['pnp-listen', startedAt.current],
    queryFn: () => fJson(`/api/phones/pnp-listen-status?since=${encodeURIComponent(startedAt.current)}`),
    refetchInterval: 2000,
  });
  useEffect(() => { const iv = setInterval(() => setElapsed(e => e + 1), 1000); return () => clearInterval(iv); }, []);

  const listener = status.data?.listener || {};
  const arrivals: any[] = status.data?.arrivals || [];
  const healthy = listener.up && listener.joined;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-start justify-center z-50 p-4 overflow-auto" onClick={onClose}>
      <div className="card max-w-xl w-full p-5 space-y-4 mt-8" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2"><Ear size={16} className="text-primary" /> Listen for a phone</h2>
          <button onClick={onClose} className="btn-ghost p-1"><X size={16} /></button>
        </div>

        {/* Listener health — a mis-joined multicast socket is the classic silent failure. */}
        <div className={`text-xs px-3 py-2 rounded-md flex items-center gap-2 ${healthy ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning'}`}>
          {healthy ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}
          {!listener.up
            ? 'PnP listener is not responding (stratus-pnp may be down). Onboarding by boot-announce will not work until it is back.'
            : listener.joined
              ? <span>Listening on multicast <span className="font-mono">{listener.group}</span> — ready. Power-cycle the phone now.</span>
              : <span>Listener is up but has NOT joined the multicast group — check the VLAN/interface. Boot announcements will not be seen.</span>}
        </div>

        <ol className="text-sm text-textSecondary space-y-1 list-decimal ml-5">
          <li>Make sure the phone/DP750 base is on the same LAN as the PBX (or its site SBC).</li>
          <li><strong>Power-cycle it now</strong> — unplug and replug, or reboot from its web UI. A DP750 only announces its exact model at boot.</li>
          <li>Watch below — it appears within ~30s of finishing boot, with its exact model and firmware.</li>
        </ol>

        <div className="border border-border rounded-md">
          <div className="px-3 py-1.5 text-xs text-textSecondary border-b border-border/60 flex items-center justify-between">
            <span>Announcements since you opened this ({elapsed}s)</span>
            <span className="font-mono">{listener.subscribes_seen != null ? `${listener.subscribes_seen} SUBSCRIBE seen` : ''}</span>
          </div>
          {arrivals.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-textSecondary flex items-center justify-center gap-2">
              <Radar size={14} className="animate-pulse text-primary" /> Waiting for a boot announcement…
            </div>
          ) : arrivals.map((a: any) => {
            const real = !!a.model && !['unknown', '?'].includes(String(a.model).toLowerCase());
            return (
              <div key={a.id} className="px-3 py-2 border-b border-border/60 last:border-0 flex items-center gap-3 text-sm">
                <CheckCircle2 size={14} className="text-success shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{real ? a.model : 'Grandstream device'}</span>
                    {isDectBaseModel(a.model) && <span className="px-1 py-0.5 rounded text-[10px] bg-success/15 text-success">DECT base</span>}
                    <span className="font-mono text-xs text-textSecondary">{(a.mac || '').replace(/:/g, '')}</span>
                  </div>
                  <div className="text-xs text-textSecondary">{a.ip}{a.firmware ? ` · fw ${a.firmware}` : ''} · {timeAgo(a.last_seen)}</div>
                </div>
                {a.status === 'assigned'
                  ? <span className="text-xs text-textSecondary shrink-0">already assigned</span>
                  : <button className="btn-primary text-xs shrink-0"
                      onClick={() => onAssign({ id: a.id, _id: a.id, mac_address: a.mac, ip_address: a.ip, model: a.model, vendor_key: 'grandstream' })}>
                      {isDectBaseModel(a.model) ? 'Set up' : 'Assign'}
                    </button>}
              </div>
            );
          })}
        </div>
        <div className="text-[11px] text-textSecondary">
          Nothing after a full boot? The phone may be on a different VLAN, or multicast is being filtered by the switch (IGMP snooping). A phone behind a branch SBC announces to that site's SBC, not here.
        </div>
      </div>
    </div>
  );
}

// Bulk CSV/paste importer — the scale path for ~90-site rollouts. Parses + validates
// locally (pure parsePnpCsv), previews every row, then POSTs the valid rows to the
// bulk-enroll API and renders per-row success/failure. Never one-at-a-time.
function BulkEnrollModal({ open, onClose, onDone }: { open: boolean; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [text, setText] = useState('');
  const [rpsMode, setRpsMode] = useState('auto');
  const [result, setResult] = useState<any | null>(null);
  const profs = useQuery({ queryKey: ['rps-profiles'], queryFn: () => api.devices.rpsProfiles(), enabled: open, staleTime: 60_000 });

  // Re-parse on every keystroke — cheap, pure, gives live validation feedback.
  const parsed = text.trim() ? parsePnpCsv(text) : { rows: [] as PnpCsvRow[], validCount: 0, invalidCount: 0 };
  const remoteNotReady = rpsMode === 'remote' && profs.data && !profs.data.remoteReady;

  const run = useMutation({
    mutationFn: () => api.devices.bulkEnroll({
      rpsProfile: rpsMode === 'auto' ? undefined : rpsMode,
      rows: parsed.rows.filter(r => r.valid).map(r => ({
        mac: r.mac, sn: r.sn, site: r.site || undefined, extension: r.extension || undefined, label: r.label || undefined,
      })),
    }),
    onSuccess: (r) => { setResult(r); onDone(); toast(r.failed ? 'info' : 'success', `Enrolled ${r.enrolled}/${r.total}${r.failed ? ` · ${r.failed} failed` : ''}`); },
    onError: (e: any) => toast('error', e.message),
  });

  const reset = () => { setText(''); setResult(null); };
  const close = () => { reset(); onClose(); };

  return (
    <Dialog open={open} onClose={close} title="Bulk enroll phones (CSV / paste)" size="xl"
      footer={result
        ? <><button onClick={reset} className="btn-ghost">Enroll more</button><button onClick={close} className="btn-primary">Done</button></>
        : <><button onClick={close} className="btn-ghost">Cancel</button>
            <button onClick={() => run.mutate()} disabled={!parsed.validCount || run.isPending}
              className="btn-primary disabled:opacity-50">{run.isPending ? 'Enrolling…' : `Enroll ${parsed.validCount} phone${parsed.validCount === 1 ? '' : 's'}`}</button></>}>
      {result ? (
        <div className="space-y-3">
          <div className="flex gap-3 text-sm">
            <span className="inline-flex items-center gap-1 text-success"><CheckCircle2 size={14} /> {result.enrolled} enrolled</span>
            {result.failed > 0 && <span className="inline-flex items-center gap-1 text-danger"><AlertTriangle size={14} /> {result.failed} failed</span>}
            <span className="text-textSecondary">of {result.total}</span>
          </div>
          <div className="card p-0 overflow-auto max-h-[55vh]">
            <table className="table">
              <thead><tr><th></th><th>MAC</th><th>SN</th><th>Site</th><th>Ext</th><th>Status</th><th>Detail</th></tr></thead>
              <tbody>
                {result.results.map((r: any) => (
                  <tr key={r.row} className="hover:bg-elevated">
                    <td>{r.ok ? <CheckCircle2 size={14} className="text-success" /> : <AlertTriangle size={14} className="text-danger" />}</td>
                    <td className="font-mono text-xs">{r.mac}</td>
                    <td className="font-mono text-xs">{r.sn || '—'}</td>
                    <td className="text-xs">{r.domain || r.site || '—'}</td>
                    <td className="text-xs">{r.extension || '—'}</td>
                    <td className="text-xs">{r.status === 'bound-elsewhere'
                      ? <span className="px-1.5 py-0.5 rounded bg-warning/15 text-warning whitespace-nowrap" title="MAC is bound in another system (e.g. the operator 3CX). Release it there; on-LAN PnP still works.">bound elsewhere</span>
                      : <span className={r.ok ? 'text-success' : 'text-danger'}>{r.status}</span>}</td>
                    <td className="text-xs text-textSecondary">{r.error || r.warning || (r.ok ? 'zero-touch redirect issued' : '')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="text-sm text-textSecondary">
            Paste rows as <span className="font-mono text-text">mac,sn,site,extension,label</span> — one phone per line
            (comma or tab; a header row and <span className="font-mono">#</span> comments are ignored). Built for provisioning many remote sites at once.
          </div>
          <textarea
            className="input font-mono text-xs w-full h-48"
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder={'mac,sn,site,extension,label\nc0:74:ad:11:22:33,20A1B2C3D4E5,Front Desk,101,Reception\nc0:74:ad:44:55:66,20A9F8E7D6C5,Back Office,102,'}
          />
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-sm text-textSecondary">RPS profile for this batch:</label>
            <select className="input w-auto" value={rpsMode} onChange={e => setRpsMode(e.target.value)}>
              <option value="auto">Auto — by each phone's site</option>
              <option value="local">Local — StratusHTTP (LAN/HTTP){profs.data?.localProfile ? ` · ${profs.data.localProfile}` : ''}</option>
              <option value="remote">Remote — public-HTTPS{profs.data?.remoteProfile ? ` · ${profs.data.remoteProfile}` : ''}</option>
            </select>
            {text.trim() && (
              <span className="text-xs text-textSecondary ml-auto">
                {parsed.validCount} valid{parsed.invalidCount ? ` · ${parsed.invalidCount} to fix` : ''}
              </span>
            )}
          </div>
          {remoteNotReady && (
            <div className="text-[11px] px-2 py-1.5 rounded bg-warning/10 text-warning flex items-start gap-1">
              <AlertTriangle size={12} className="mt-0.5 shrink-0" />
              No public-HTTPS RPS profile configured yet — remote enrolls will be queued until it's added in the GDMS portal (set GDMS_RPS_PROFILE_REMOTE).
            </div>
          )}
          {text.trim() && (
            <div className="card p-0 overflow-auto max-h-[40vh]">
              <table className="table">
                <thead><tr><th></th><th>MAC</th><th>SN</th><th>Site</th><th>Ext</th><th>Label</th><th>Issue</th></tr></thead>
                <tbody>
                  {parsed.rows.map(r => (
                    <tr key={r.line} className={`hover:bg-elevated ${r.valid ? '' : 'bg-danger/5'}`}>
                      <td>{r.valid ? <CheckCircle2 size={14} className="text-success" /> : <AlertTriangle size={14} className="text-danger" />}</td>
                      <td className="font-mono text-xs">{r.mac || r.macRaw || '—'}</td>
                      <td className="font-mono text-xs">{r.sn || '—'}</td>
                      <td className="text-xs">{r.site || '—'}</td>
                      <td className="text-xs">{r.extension || '—'}</td>
                      <td className="text-xs">{r.label || '—'}</td>
                      <td className="text-xs text-danger">{r.errors.join('; ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
}

function AssignForm({ target, setTarget, users, tenants }: any) {
  const [dom, setDom] = useState(target.bound_domain_uuid || '');
  const [ext, setExt] = useState('');
  const [label, setLabel] = useState('');
  const [model, setModel] = useState('');
  const [sn, setSn] = useState('');
  // Remote phones (behind an SBC / not on the PBX LAN) default to the public-HTTPS
  // RPS profile; Local (PBX) phones to the LAN StratusHTTP profile. 'auto' lets the
  // server apply that policy from the phone's site.
  const isRemoteSite = !!target.sbc_name && target.sbc_name !== 'Local (PBX)';
  const [rps, setRps] = useState('auto');
  // Since #684 (PnP relay), Local (PBX) and SBC-bound phones provision via multicast
  // PnP with no serial — SN/RPS is only required for a no-SBC network (drop-ship /
  // home worker). Show the GDMS fields by default only in that case; otherwise tuck
  // them behind the Advanced toggle so operators don't hunt for a serial they don't need.
  const gdmsNeeded = needsGdmsZeroTouch(target.sbc_name);
  const [showGdms, setShowGdms] = useState(gdmsNeeded);
  const inDept = users.filter((u: any) => u.domain_uuid === dom);
  const profs = useQuery({ queryKey: ['rps-profiles'], queryFn: () => api.devices.rpsProfiles(), staleTime: 60_000 });
  // Seed the site-derived RPS default onto the target once so it threads on Assign
  // even if the operator never opens the picker.
  useEffect(() => { setTarget((t: any) => ({ ...t, _id: t.id, _rps: 'auto', _remote: isRemoteSite })); }, []);
  // Effective remote flag for the chosen RPS mode (auto follows the site).
  const effRemote = rps === 'remote' ? true : rps === 'local' ? false : isRemoteSite;
  const remoteNotReady = effRemote && profs.data && !profs.data.remoteReady;
  // DECT base (#799): the base is infrastructure — it can be set up with NO user.
  // The extension picker becomes optional ("handset 1"); more handsets are attached
  // afterwards from Phones → row menu → Handsets.
  const dectBase = isDectBaseModel(model);
  const push = (patch: any) => setTarget({ ...target, _id: target.id, _dom: dom, _ext: ext, _label: label, _model: model, _sn: sn, _remote: effRemote, _rps: rps, _dectBase: dectBase, ...patch });
  return (
    <div className="space-y-3">
      <div className="text-xs text-textSecondary">
        Phone: <span className="font-mono">{target.mac_address}</span> ({target.vendor_key} {target.model})
        {target.sbc_name && <span className="ml-2 px-1.5 py-0.5 rounded bg-primary/10 text-primary">{target.sbc_name === 'Local (PBX)' ? 'Local (PBX)' : target.sbc_name}</span>}
      </div>
      <div>
        {/* Auto-detect is unreliable (often 'unknown'), so the admin picks the model — this selects
            the provisioning template. Without it the phone reboots but never gets a config. */}
        <label className="block text-sm text-textSecondary mb-1">Phone model</label>
        <select className="input" value={model} onChange={e => { const m = e.target.value; setModel(m); push({ _model: m, _dectBase: isDectBaseModel(m) }); }}>
          <option value="">— Choose model —</option>
          {prioritiseVendor(target.vendor_key).map(g => (
            <optgroup key={g.group} label={g.group}>
              {g.models.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </optgroup>
          ))}
        </select>
      </div>
      {dectBase && (
        <div className="text-[11px] px-2 py-1.5 rounded bg-primary/10 text-primary">
          A DECT base is infrastructure, not a user's phone: pick the department, set the base up,
          then attach each DP720 handset to its user from <strong>Phones → Handsets</strong>.
          Optionally pick a user below to attach handset 1 right away.
        </div>
      )}
      <div>
        <label className="block text-sm text-textSecondary mb-1">Department</label>
        <select className="input" value={dom} onChange={e => { setDom(e.target.value); setExt(''); push({ _dom: e.target.value, _ext: '' }); }}>
          <option value="">— Choose —</option>
          {tenants.map((t: any) => <option key={t.domain_uuid} value={t.domain_uuid}>{t.domain_description || t.domain_name}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-sm text-textSecondary mb-1">{dectBase ? 'Handset 1 user (optional — attach handsets later)' : 'Extension'}</label>
        <select className="input" value={ext} onChange={e => { setExt(e.target.value); push({ _ext: e.target.value }); }} disabled={!dom}>
          <option value="">{dectBase ? '— None (set up base only) —' : '— Choose —'}</option>
          {inDept.map((u: any) => <option key={u.extension_uuid} value={u.extension_uuid}>{u.extension} — {u.name}</option>)}
        </select>
      </div>
      {/* GDMS zero-touch (SN + RPS). A Local (PBX) / SBC-bound phone provisions via
          multicast PnP (#684) and needs neither — collapse the section behind the
          Advanced toggle for those. A phone on an unmatched subnet (no SBC, e.g.
          drop-ship / home worker) can ONLY zero-touch via GDMS RPS, so it's expanded. */}
      {!gdmsNeeded && (
        <button type="button" onClick={() => setShowGdms(s => !s)}
          className="text-sm text-textSecondary hover:text-text inline-flex items-center gap-1"
          title="This phone provisions via PnP (Local/SBC) — no serial needed. Open only to enroll it in GDMS RPS zero-touch anyway.">
          <ChevronRight size={14} className={`transition-transform ${showGdms ? 'rotate-90' : ''}`} />
          Advanced: GDMS zero-touch (RPS)
        </button>
      )}
      {showGdms && (
        <>
          <div>
            {/* Serial number — GDMS device/add REQUIRES a real MAC + SN (placeholders are
                rejected). Needed for zero-touch enroll of factory / remote drop-ship phones. */}
            <label className="block text-sm text-textSecondary mb-1">Serial number (SN) <span className="text-textSecondary/70">— for GDMS zero-touch</span></label>
            <input className="input font-mono" value={sn} onChange={e => { setSn(e.target.value.trim()); push({ _sn: e.target.value.trim() }); }}
              placeholder="Scan barcode or type — e.g. 20A1B2C3D4E5" autoComplete="off" />
            <div className="text-[11px] text-textSecondary mt-1">From the box/label. Leave blank to assign without GDMS enrollment ({gdmsNeeded ? 'the phone must then be pointed at the PBX by hand' : 'PnP provisions this phone either way'}).</div>
          </div>
          <div>
            {/* Per-site RPS profile: which org-scoped GDMS profile the phone's redirect
                uses. Remote sites MUST use the public-HTTPS profile (a LAN/HTTP profile
                is unreachable from a branch office). */}
            <label className="block text-sm text-textSecondary mb-1">RPS profile</label>
            <select className="input" value={rps} onChange={e => { setRps(e.target.value); const er = e.target.value === 'remote' ? true : e.target.value === 'local' ? false : isRemoteSite; push({ _rps: e.target.value, _remote: er }); }}>
              <option value="auto">Auto — by site ({isRemoteSite ? 'remote → public-HTTPS' : 'local → StratusHTTP'})</option>
              <option value="local">Local — StratusHTTP (LAN/HTTP){profs.data?.localProfile ? ` · ${profs.data.localProfile}` : ''}</option>
              <option value="remote">Remote — public-HTTPS{profs.data?.remoteProfile ? ` · ${profs.data.remoteProfile}` : ''}</option>
            </select>
            {remoteNotReady && (
              <div className="mt-1.5 text-[11px] px-2 py-1.5 rounded bg-warning/10 text-warning flex items-start gap-1">
                <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                No public-HTTPS RPS profile is configured yet (set GDMS_RPS_PROFILE_REMOTE + add the profile in the GDMS portal). A remote phone can't reach the LAN-only profile, so enroll will be queued until it's in place.
              </div>
            )}
          </div>
        </>
      )}
      <div>
        <label className="block text-sm text-textSecondary mb-1">Label (optional)</label>
        <input className="input" value={label} onChange={e => { setLabel(e.target.value); push({ _label: e.target.value }); }} placeholder="Front desk Yealink" />
      </div>
    </div>
  );
}
